import {
  Box,
  LinearProgress,
  MenuItem,
  Pagination,
  Select,
  Typography,
} from "@mui/material";
import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import QuizIcon from "../../../../../../assets/icons/quiz-icon-overview.svg";
import CustomClickableQuestionsCard from "../../../../../../customComponents/CustomClickableQuestionsCard";
import { useGetQuizzesPaginatedQuery } from "../../../../../../app/quizApiSlice";
import { useGetTeacherSubjectsQuery } from "../../../../../../app/questionApiSlice";
import { useGetSubjectsPaginatedQuery } from "../../../../subjects/subjectsApiSlice";
import { useGetClassesPaginatedQuery } from "../../../../classroom/classApiSlice";
import { selectCurrentUser } from "../../../../auth/authSlice";

const QuizReportList = () => {
  const [page, setPage] = useState(1);
  const [filterSubject, setFilterSubject] = useState("all");
  const [filterLevel, setFilterLevel] = useState("all");
  const [sortBy, setSortBy] = useState("date");
  const itemsPerPage = 10;
  const navigate = useNavigate();

  const currentUser = useSelector(selectCurrentUser);
  const userRoles = currentUser?.roles?.map((r) => r?.role?.name).filter(Boolean) || [];
  const isAdminOrExaminer = userRoles.includes("admin") || userRoles.includes("examiner");

  const { data: teacherSubjectsData } = useGetTeacherSubjectsQuery(undefined, { skip: isAdminOrExaminer });
  const { data: subjectsData } = useGetSubjectsPaginatedQuery({ page: 1, per_page: 200 }, { skip: !isAdminOrExaminer });
  const { data: classroomsData } = useGetClassesPaginatedQuery({ page: 1, per_page: 200 }, { skip: !isAdminOrExaminer });

  const { data, isLoading, isFetching } = useGetQuizzesPaginatedQuery({
    page,
    per_page: itemsPerPage,
    subject: filterSubject !== "all" ? filterSubject : null,
    level: filterLevel !== "all" ? filterLevel : null,
    sort_by: sortBy,
    has_submissions: true,
  });

  const quizzes = (data?.quizzes || []).filter((q) => q.school_id);

  const totalQuizzes = data?.pagination?.total || 0;
  const totalPages = data?.pagination?.pages || 0;
  const startIndex = (page - 1) * itemsPerPage + 1;
  const endIndex = Math.min(page * itemsPerPage, totalQuizzes);

  const uniqueSubjects = React.useMemo(() => {
    if (isAdminOrExaminer) {
      if (!subjectsData?.subjects) return [];
      return [...new Set(subjectsData.subjects.map((s) => s.name).filter(Boolean))].sort();
    }
    if (!teacherSubjectsData) return [];
    return [...new Set(teacherSubjectsData.map((c) => c.name))].filter(Boolean).sort();
  }, [isAdminOrExaminer, teacherSubjectsData, subjectsData]);

  const uniqueLevels = React.useMemo(() => {
    if (isAdminOrExaminer) {
      if (!classroomsData?.classrooms) return [];
      return [...new Set(classroomsData.classrooms.map((c) => c.level).filter(Boolean))].sort();
    }
    if (!teacherSubjectsData) return [];
    return [...new Set(teacherSubjectsData.map((c) => c.level))].filter(Boolean).sort();
  }, [isAdminOrExaminer, teacherSubjectsData, classroomsData]);

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return "";
    return date.toLocaleDateString("en-US", { year: "numeric", month: "2-digit", day: "2-digit" });
  };

  return (
    <Box>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: "1em",
          gap: 2,
          flexWrap: "wrap",
        }}
      >
        <Typography
          variant="h3"
          sx={{ fontSize: { xs: "1rem", md: "1.25rem" }, fontWeight: 700, letterSpacing: "-1px" }}
        >
          Quiz Reports
        </Typography>
        <Box sx={{ display: "flex", gap: 1.5, flexWrap: "wrap" }}>
          <Select
            value={filterSubject}
            onChange={(e) => { setFilterSubject(e.target.value); setPage(1); }}
            size="small"
            displayEmpty
            sx={{ minWidth: 120 }}
          >
            <MenuItem value="all">All Subjects</MenuItem>
            {uniqueSubjects.map((s) => <MenuItem key={s} value={s}>{s}</MenuItem>)}
          </Select>
          <Select
            value={filterLevel}
            onChange={(e) => { setFilterLevel(e.target.value); setPage(1); }}
            size="small"
            displayEmpty
            sx={{ minWidth: 120 }}
          >
            <MenuItem value="all">All Levels</MenuItem>
            {uniqueLevels.map((l) => <MenuItem key={l} value={l}>{l}</MenuItem>)}
          </Select>
          <Select
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value)}
            size="small"
            sx={{ minWidth: 120 }}
          >
            <MenuItem value="date">Sort by: Date</MenuItem>
            <MenuItem value="subject">Sort by: Subject</MenuItem>
            <MenuItem value="level">Sort by: Level</MenuItem>
          </Select>
        </Box>
      </Box>

      {(isLoading || isFetching) && <Box sx={{ width: "100%", mb: 2 }}><LinearProgress /></Box>}

      {quizzes.length === 0 && !isLoading && (
        <Box textAlign="center" py={4}>
          <Typography color="text.secondary">No quizzes found</Typography>
        </Box>
      )}

      {quizzes.map((quiz) => (
        <CustomClickableQuestionsCard
          key={quiz.id}
          icon={QuizIcon}
          title={`${quiz.subject} — ${quiz.level}`}
          details={`${quiz.type}`}
          date={formatDate(quiz.updated_at)}
          status={quiz.type}
          navigateTo={`/lms/quizzes/report-list/${quiz.id}/submissions`}
          cardData={quiz}
        />
      ))}

      <Box sx={{ display: "flex", justifyContent: "space-between", marginTop: "2em", alignItems: "center" }}>
        <Typography variant="body2" color="text.secondary">
          Showing {totalQuizzes > 0 ? startIndex : 0} to {endIndex} of {totalQuizzes} entries
        </Typography>
        <Pagination
          count={totalPages}
          page={page}
          onChange={(_e, newPage) => setPage(newPage)}
          color="primary"
          shape="rounded"
        />
      </Box>
    </Box>
  );
};

export default QuizReportList;

